import { CONTAINER_CLASS, Section } from "@/components/sections/Section";
import { cn } from "@/lib/utils";
import type { Section as SectionType } from "@/types/global";

type ProseSection = Extract<SectionType, { type: "prose" }>;

function ProseContent({ html }: { html: string }) {
  return (
    <div
      className="prose prose-invert max-w-none prose-headings:font-display prose-headings:uppercase prose-headings:tracking-tight prose-a:no-underline"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function ProseTitle({ title }: { title: string }) {
  if (!title) {
    return null;
  }

  return (
    <>
      <h2 className="heading-jumbo-small">{title}</h2>
      <span className="separateur" />
    </>
  );
}

export function Prose({ settings, data }: ProseSection) {
  const { title, content, image, variant, reverse } = data;

  const body = (
    <>
      <ProseTitle title={title} />
      <div className={cn(title && "mt-6")}>
        <ProseContent html={content} />
      </div>
    </>
  );

  if (variant === "title_left") {
    if (title) {
      return (
        <Section settings={settings}>
          <div className="grid gap-10 lg:grid-cols-3 lg:gap-16 items-start">
            <div className="lg:col-span-1">
              <ProseTitle title={title} />
            </div>
            <div className="lg:col-span-2">
              <ProseContent html={content} />
            </div>
          </div>
        </Section>
      );
    }

    return (
      <Section settings={settings}>
        <ProseContent html={content} />
      </Section>
    );
  }

  if (!image || (variant !== "image_side" && variant !== "image_background")) {
    return (
      <Section settings={settings}>
        {body}
      </Section>
    );
  }

  if (variant === "image_background") {
    return (
      <Section settings={settings} contained={false} className="min-h-[480px] flex items-center">
        <img
          src={image.url}
          alt={image.alt}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: image.focus_point }}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-background/30" aria-hidden />
        <div
          className={cn(
            CONTAINER_CLASS[settings.container],
            "relative z-10 flex",
            reverse ? "justify-end" : "justify-start",
          )}
        >
          <div className="max-w-xl bg-background/80 p-8 md:p-10">
            {body}
          </div>
        </div>
      </Section>
    );
  }

  // variant === "image_side"
  return (
    <Section settings={settings}>
      <div
        className={cn(
          "grid gap-10 md:gap-16 items-center md:grid-cols-2",
          reverse && "md:[&>:first-child]:order-2",
        )}
      >
        <div>{body}</div>
        <img
          src={image.url}
          alt={image.alt}
          width={image.width || undefined}
          height={image.height || undefined}
          className="h-72 md:h-96 w-full object-cover"
          style={{ objectPosition: image.focus_point }}
          loading="lazy"
        />
      </div>
    </Section>
  );
}
